import { useEntryMutations } from "@app/hooks/useEntryMutations";
import { useOrganisation } from "@app/hooks/useOrganisation";
import { useProject } from "@app/hooks/useProject";
import { useTiming } from "@app/hooks/useTiming";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
	AnnualSupportHoursTag,
	CommencementDateTag,
	ContractTerm,
	SubscriptionStartDate,
} from "../api/CapsuleParties";
import type { ProjectSummary } from "../interfaces/ProjectSummary";
import type { UserDetails } from "../interfaces/UserDetails";
import { ActivityButton } from "./ActivityButton";
import { DateInput } from "./DateInput";
import { EntrySummaryCard } from "./EntrySummaryCard";
import { MinOverrideFilter } from "./MinOverrideFilter";
import { ProjectSummaryHeader } from "./ProjectSummaryHeader";
import {
	ClearTiming,
	GetSetProjectTime,
	type ProjectTime,
	SaveTiming,
	type Timing,
	TimingPanel,
	ToggleTimer,
} from "./Timing";
import { WithQuery, WithSpinner } from "./WithQuery";

function SetWorkDate(
	project: ProjectSummary,
	work_date: string,
	t: Timing,
	refreshTiming: () => void,
) {
	const projectTime = GetSetProjectTime(project.project_id, project.id, t);
	projectTime.work_date = work_date;
	t.projects[project.project_id] = projectTime;
	SaveTiming(t);
	refreshTiming();
}

function SetActivity(
	project: ProjectSummary,
	activity: string,
	t: Timing,
	refreshTiming: () => void,
) {
	const projectTime = GetSetProjectTime(project.project_id, project.id, t);
	projectTime.activity = activity;
	t.projects[project.project_id] = projectTime;
	SaveTiming(t);
	refreshTiming();
}

function SetDescription(
	project: ProjectSummary,
	description: string,
	t: Timing,
	refreshTiming: () => void,
) {
	const projectTime = GetSetProjectTime(project.project_id, project.id, t);
	projectTime.work_desc = description;
	t.projects[project.project_id] = projectTime;
	SaveTiming(t);
	refreshTiming();
}

function SetRequisitioner(
	project: ProjectSummary,
	requisitioner: string | undefined,
	t: Timing,
	refreshTiming: () => void,
) {
	const projectTime = GetSetProjectTime(project.project_id, project.id, t);
	projectTime.requisitioner = requisitioner;
	t.projects[project.project_id] = projectTime;
	SaveTiming(t);
	refreshTiming();
}

function SetMinOverride(
	project: ProjectSummary,
	override: boolean,
	t: Timing,
	refreshTiming: () => void,
) {
	const projectTime = GetSetProjectTime(project.project_id, project.id, t);
	projectTime.override_minimum = override;
	t.projects[project.project_id] = projectTime;
	SaveTiming(t);
	refreshTiming();
}

export const ProjectCard = ({ user }: { user: UserDetails }) => {
	const [searchParams] = useSearchParams();

	const projectID = searchParams.get("project");

	const project = useProject(projectID);

	return (
		<WithQuery
			query={project}
			showLoading={true}
			render={(projectData: ProjectSummary | null) => {
				if (!projectData) return null;
				return <NewProjectCard projectData={projectData} user={user} />;
			}}
		/>
	);
};

const NewProjectCard = ({
	projectData,
	user,
}: {
	projectData: ProjectSummary;
	user: UserDetails;
}) => {
	const [searchParams, setSearchParams] = useSearchParams();

	const [_, setCardData] = useState<number>(0);

	const logging = searchParams.get("h") !== "true";

	const { organisation, organisationPeople } = useOrganisation(
		projectData.capsule_id,
	);

	const { postEntryQuery } = useEntryMutations();

	const timing = useTiming();

	const [supportHours, commencementDate, contractTerm] = useMemo(() => {
		let supportHours: string | undefined;
		let commencementDate: Date | undefined;
		let contractTerm: string | undefined;

		if (organisation.data == null || organisation.data.fields == null) {
			return [supportHours, commencementDate, contractTerm];
		}

		organisation.data.fields.forEach((f) => {
			if (projectData.project_id.endsWith("ISP")) {
				if (f.definition.id === CommencementDateTag) {
					commencementDate = new Date(f.value);
				}
				if (f.definition.id === ContractTerm) {
					contractTerm = f.value;
				}
				if (f.definition.id === AnnualSupportHoursTag) {
					supportHours = f.value;
				}
			}
			if (
				projectData.project_id.endsWith("Metrix") &&
				f.definition.id === SubscriptionStartDate
			) {
				commencementDate = new Date(f.value);
			}
		});

		return [supportHours || "7", commencementDate, contractTerm || "1 Year"];
	}, [organisation.data, projectData.project_id]);

	function refreshTiming() {
		timing.refetch();
		setCardData((v) => v + 1);
	}

	function partyLink() {
		window.open(
			"https://chartistechnology.capsulecrm.com/party/" +
				projectData.capsule_id,
		);
	}

	function projectLink() {
		window.open(
			`https://chartistechnology.capsulecrm.com/project/${projectData.id}`,
		);
	}

	if (timing.data == null) {
		return null;
	}

	const projectTiming = GetSetProjectTime(
		projectData.project_id,
		projectData.id,
		timing.data,
	);

	return (
		<WithSpinner loading={postEntryQuery.isPending}>
			<div key={"header"} className={"page-header"}>
				<div className={"flex flex-row"}>
					<img
						className={"logo with-margin"}
						src={"/chartis_logo.png"}
						alt={"logo"}
					/>
					<div className={"flex-col"}>
						<h2
							onClick={partyLink}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									partyLink();
								}
							}}
							className={"header-with-sub cursor with-link"}
						>
							{projectData.org_name}
						</h2>
						<button
							type={"button"}
							className={"button-div italic sub-header"}
							onClick={projectLink}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									projectLink();
								}
							}}
						>
							{`${projectData.project_id}: ${projectData.project_reference} `}
							{commencementDate != null ? (
								<>
									(ISP Anniversary: {commencementDate.toLocaleDateString()} -
									Annual Support Hours: {supportHours} - Contract Term:{" "}
									{contractTerm})
								</>
							) : (
								<>(No ISP Details)</>
							)}
						</button>
					</div>
				</div>
				<button
					type={"button"}
					className={"button button-success"}
					onClick={() => {
						logging
							? setSearchParams(
									{ project: projectData.id.toFixed(), h: "true" },
									{ replace: true },
								)
							: setSearchParams(
									{ project: projectData.id.toFixed() },
									{ replace: true },
								);
					}}
				>
					{logging ? "Show History" : "Back to Card"}
				</button>
				<div className={"margin-right"}>
					<button
						type={"button"}
						className={"button button-primary"}
						onClick={() => {
							if (timing.data.start_time != null) {
								SaveTiming(
									ToggleTimer(
										projectData.project_id,
										projectData.id,
										timing.data,
									),
								);
							}
							if (projectTiming.run_time === 0) {
								ClearTiming(projectData.project_id);
							}
							window.history.back();
						}}
					>
						{timing.data.start_time != null ? "Pause & Go Back" : "Go Back"}
					</button>
				</div>
			</div>
			{logging ? (
				<div key={"project_card"} className={"padded"}>
					<div key="new-activity-card" className={"flex flex-col card padded"}>
						<ProjectSummaryHeader
							project={projectData}
							text={"New Activity Card"}
						/>
						<div className="flex-row">
							<div className={"flex flex-col padded-right"}>
								<label htmlFor={"activity-buttons"}>Activity</label>
								<div className="flex flex-row">
									<ActivityButton
										name={"Service"}
										active={projectTiming.activity}
										setActive={(v) =>
											SetActivity(projectData, v, timing.data, refreshTiming)
										}
									/>
									<ActivityButton
										name={"Phone"}
										active={projectTiming.activity}
										setActive={(v) =>
											SetActivity(projectData, v, timing.data, refreshTiming)
										}
									/>
									<ActivityButton
										name={"Support"}
										active={projectTiming.activity}
										setActive={(v) =>
											SetActivity(projectData, v, timing.data, refreshTiming)
										}
									/>
									<ActivityButton
										name={"Training"}
										active={projectTiming.activity}
										setActive={(v) =>
											SetActivity(projectData, v, timing.data, refreshTiming)
										}
									/>
								</div>
							</div>
							<div className={"flex flex-col padded-right"}>
								<label htmlFor={"work-date"}>Date/Time of Work:</label>
								<DateInput
									id={"work-date"}
									date={projectTiming.work_date}
									setDate={(v) =>
										SetWorkDate(projectData, v, timing.data, refreshTiming)
									}
								/>
							</div>
							<MinOverrideFilter
								time={projectTiming}
								setTime={(v: boolean) =>
									SetMinOverride(projectData, v, timing.data, refreshTiming)
								}
							/>
						</div>
						<label htmlFor={"description-of-work"}>Description of Work:</label>
						<textarea
							id={"description-of-work"}
							className={"input"}
							value={projectTiming.work_desc}
							onChange={(e) => {
								const v = e.currentTarget.value;
								SetDescription(projectData, v, timing.data, refreshTiming);
							}}
						/>
						<label htmlFor={"requisitioner-select"}>
							Requisitioner (Optional):
						</label>
						<select
							id={"requisitioner-select"}
							className={"input"}
							value={projectTiming.requisitioner || ""}
							onChange={(e) => {
								if (e.target.value === "") {
									return SetRequisitioner(
										projectData,
										undefined,
										timing.data,
										refreshTiming,
									);
								}
								return SetRequisitioner(
									projectData,
									e.target.value,
									timing.data,
									refreshTiming,
								);
							}}
						>
							<option key={"-"} value={""}>
								-
							</option>
							{(organisationPeople.data ?? []).map((p) => {
								const name = `${p.firstName} ${p.lastName} (${p.jobTitle})`;
								return (
									<option key={p.id} value={name}>
										{name}
									</option>
								);
							})}
						</select>
						<TimingPanel
							saving={postEntryQuery.isPending}
							projectID={projectData.project_id}
							linkID={projectData.id}
							canSave={
								projectTiming.work_desc !== "" &&
								(projectTiming.run_time !== 0 ||
									timing.data.start_time != null ||
									projectTiming.override_hours != null ||
									projectTiming.override_minutes != null)
							}
							onSave={(t: ProjectTime) => {
								postEntryQuery.mutate({
									timing: t,
									project: projectData,
									user: user,
								});
							}}
						/>
					</div>
				</div>
			) : (
				<EntrySummaryCard
					key={"history"}
					project={projectData}
					peopleData={organisationPeople.data ?? []}
					supportHours={supportHours}
					commencementDate={commencementDate}
					contractTerm={contractTerm}
				/>
			)}
		</WithSpinner>
	);
};
